import type { UiForm, UiInput, UiTypes } from "various-ui";

export const useComposable = (emit: { (event: "redraw", data: any): void }) => {
    //* 获取I18n
    const i18n = useI18n();

    //* 响应式变量
    const refs = {
        show: ref<boolean>(false),

        dictionaries: ref<any>({}), //? 字典
        candidate: ref<UiTypes.candidate[]>([]), //? 候选项
        original: ref<any>({}), //? 原始数据

        cPlaceholder: { start: i18n.t("BioTools.StartSite"), end: i18n.t("BioTools.EndSite") },
    };

    //* 表单数据
    const data = reactive({
        ref: "",
        alt: "",
        coordinate: <{ start?: number; end?: number }>{},
    });

    //* 节点
    const nodes = {
        alt: ref<InstanceType<typeof UiInput>>(),
        form: ref<InstanceType<typeof UiForm>>(),
    };

    //* 表单校验规则
    const rules: { [name: string]: UiTypes.verifyRule[] } = {
        coordinate: [
            {
                trigger: "change",
                verify: (data: any) => {
                    const seqLen = refs.dictionaries.value?.sequence?.length || 0;
                    const start = Number(data.coordinate.start);
                    const end = Number(data.coordinate.end);
                    /**当start, end为0或者不存在时 */
                    if (!start || !end) {
                        return { verify: false, message: i18n.t("CoordinateMustBeLargerThan0") };
                    } /**当起始坐标超出范围时 */ else if (start < 1 || start > seqLen) {
                        return {
                            verify: false,
                            message: i18n.t("BioTools.PositionVerify", { type: i18n.t("BioTools.Start"), start: 1, end: seqLen }),
                        };
                    } /**当终止坐标超出范围时 */ else if (end < 1 || end > seqLen) {
                        return {
                            verify: false,
                            message: i18n.t("BioTools.PositionVerify", { type: i18n.t("BioTools.End"), start: 1, end: seqLen }),
                        };
                    } /**当起始大于结束时 */ else if (start > end) {
                        return { verify: false, message: i18n.t("Placeholder.StartExceedEnd") };
                    } /**当片段大于20bp时 */ else if (end - start + 1 > 50) {
                        return { verify: false, message: i18n.t("BioTools.MrnaVerify1", { length: 50 }) };
                    } else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
        ref: [
            {
                trigger: "verify",
                verify: (data: any) => {
                    if (data.ref) return { verify: true, message: "" };
                    else {
                        return { verify: false, message: "Please provide the coordinate" };
                    }
                },
            },
        ],
        alt: [
            {
                trigger: "verify",
                verify: (data: any) => {
                    if (data.alt && !/^([a|A|t|T|c|C|g|G]*)$/.test(data.alt)) {
                        return { verify: false, message: "only alphabet 'ATCG' are allowed" };
                    } else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: (origin: any, dictionaries: any) => {
            //* 重置表单
            nodes.form.value?.reset();

            //* 数据初始化
            data.ref = "";
            data.alt = "";
            data.coordinate.end = undefined;
            data.coordinate.start = undefined;

            //* 数据缓存
            refs.original.value = origin || {};
            refs.dictionaries.value = dictionaries || {};
        },

        //* 焦点获取函数
        focus: () => {
            if (refs.show.value) return;
            else {
                requestAnimationFrame(() => nodes.alt.value?.focus());
            }
        },

        //* 切换数据
        changeData: function (origin: any) {
            //* 重置表单
            nodes.form.value?.reset();

            //* 初始化基础数据
            const end = origin?.range?.[1] || "";
            const start = origin?.range?.[0] || "";
            const ref = refs.dictionaries.value.sequence.slice(start - 1, end);

            //* 初始化表单数据
            refs.candidate.value = [];
            data.coordinate.end = end;
            data.coordinate.start = start;
            data.ref = ref;
            data.alt = origin.alt;

            //* 检测点位是否合规
            nodes.form.value?.validator((valid: boolean) => {
                //* 校验失败, 退出
                if (!valid) return;
                //* 存在候选菜单
                if (origin?.codons?.length) {
                    //* 生成Codons候选菜单
                    //* 遍历生成候选列表并计算最接近的alt
                    let altNumber = 0;
                    origin.codons.forEach((alt: string) => {
                        //* 补充候选列表
                        refs.candidate.value.push({ label: alt, value: alt });
                        //* 临时计数变量
                        let number = 0;
                        //* 剪接字符列表
                        const chars = alt?.split("") || [];
                        //* 遍历计数
                        for (let i = 0; i < chars.length; i++) {
                            if (ref == alt || !ref.charAt(i) || !alt.charAt(i)) return;
                            if (ref.charAt(i) == alt.charAt(i)) number++;
                        }
                        //* 检测是否大于上个数
                        if (number > altNumber) {
                            data.alt = alt;
                        }
                    });

                    //* 检测是否存在alt
                    if (!data.alt) {
                        data.alt = refs.candidate.value?.[0]?.value || "";
                    }
                }

                //* 触发预览图更新
                emit("redraw", { start: data.coordinate.start, end: data.coordinate.end, alt: data.alt, ref: data.ref });
            });
        },

        //* 切换预测位点
        changeCoordinate: function () {
            const hanlder = (valid: boolean) => {
                const start = Number(data.coordinate.start) || 0;
                const end = Number(data.coordinate.end) || 0;
                if (valid) {
                    data.ref = refs.dictionaries.value.sequence.slice(start - 1, end);
                    emit("redraw", {
                        start: start,
                        end: end,
                        alt: data.alt && /^([A|T|C|G]*)$/.test(data.alt) ? data.alt : "",
                        ref: data.ref,
                    });
                    nodes.form.value?.validator(() => {}, ["ref"]);
                } else {
                    data.ref = "";
                }
            };

            nodes.form.value?.validator(hanlder, ["coordinate"]);
        },

        //* 切换Alteration
        changeAlt: function () {
            const hanlder = (valid: boolean) => {
                if (valid) {
                    emit("redraw", { start: data.coordinate.start, end: data.coordinate.end, alt: data.alt, ref: data.ref });
                }
            };

            data.alt = data.alt.toUpperCase();
            if (data.coordinate.start && data.coordinate.end) {
                nodes.form.value?.validator(hanlder, ["coordinate", "alt"]);
            } else {
                nodes.form.value?.validator(() => {}, ["alt"]);
            }
        },

        //* 表单提交事件
        submit: function (callBack: (data: any) => void) {
            nodes.form.value?.validator((valid: boolean) => {
                valid && callBack && callBack({ alt: data.alt, ref: data.ref, gene_pos: data.coordinate.start });
            });
        },
    };

    return { refs, data, nodes, rules, methods };
};
